import L from 'leaflet';

/**
 * Fælles opsætning for Leaflet-kortet i PWA'en.
 *
 * Baggrund: CARTO lukkede for anonym brug af deres basemaps og serverer nu
 * fliser med et "API KEY REQUIRED"-vandmærke, så vi er skiftet til OpenStreetMap.
 * OSM kræver synlig kreditering - attribution-kontrollen må derfor ikke skjules.
 */
export const OSRM_SERVICE_URL = 'https://tdkosrmmapsservice.azurewebsites.net/api/TdkOsrmMapsService';

/**
 * leaflet-routing-machine sender kun continue_straight videre til OSRM, hvis
 * allowUTurns er sat (se osrm-v1.js: buildRouteUrl). Uden den bruger OSRM sin
 * default, hvor U-vending ved via-punkter er forbudt: er et støjfyldt GPS-punkt
 * snappet til en sidegade, SKAL ruten køre ind i gaden, rundt om blokken og ud
 * igen. Det er årsagen til de vilkårlige omveje ind ad sidegader.
 */
export const OSRM_ROUTING_OPTIONS = {
  alternatives: false,
  allowUTurns: true
};

const OSM_TILE_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
const OSM_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';

/** Lægger standard-baggrundskortet på et kort. */
export function addBaseTileLayer (map: L.Map): L.TileLayer {
  return L.tileLayer(OSM_TILE_URL, {
    attribution: OSM_ATTRIBUTION,
    maxZoom: 19
  }).addTo(map);
}

/**
 * Leaflet måler kun containerens størrelse ved oprettelse og ved window-resize.
 * Kortet sidder i et flex-layout, der først falder på plads når data er hentet -
 * uden dette hentes fliser ikke til det nye område (grå felter), og klik rammer
 * forskudt. Returnerer observeren, så kalderen kan disconnecte den.
 */
export function observeMapResize (map: L.Map, element: HTMLElement): ResizeObserver | null {
  if (typeof ResizeObserver === 'undefined') {
    return null;
  }

  const observer = new ResizeObserver(() => map.invalidateSize({ animate: false }));
  observer.observe(element);
  return observer;
}

/**
 * Gemte sikre ruter er rå GPS-spor med punkter få meter fra hinanden. Sendes de
 * 1:1 til OSRM som via-punkter, snapper hvert punkt til nærmeste vej, og støj
 * omkring kryds giver forkerte snap og omveje. Udtynder sporet, så punkterne
 * ligger mindst minMeters fra hinanden. Endepunkterne bevares altid.
 */
export function thinRouteWaypoints (
  points: L.LatLng[],
  minMeters = 50,
  maxPoints = 150
): L.LatLng[] {
  if (points.length <= 2) {
    return [...points];
  }

  const thinned: L.LatLng[] = [points[0]];

  for (let i = 1; i < points.length - 1; i++) {
    if (points[i].distanceTo(thinned[thinned.length - 1]) >= minMeters) {
      thinned.push(points[i]);
    }
  }

  thinned.push(points[points.length - 1]);

  return capWaypoints(thinned, maxPoints);
}

/** OSRM afviser meget lange via-lister. Udtager jævnt fordelte punkter som sikkerhedsventil. */
function capWaypoints (points: L.LatLng[], maxPoints: number): L.LatLng[] {
  if (points.length <= maxPoints) {
    return [...points];
  }

  const step = (points.length - 1) / (maxPoints - 1);
  const capped: L.LatLng[] = [];

  for (let i = 0; i < maxPoints; i++) {
    capped.push(points[Math.round(i * step)]);
  }

  return capped;
}
